import { COURIER } from '@hive/status'
import { GQL_ID } from '@@partner:types:graphql'
import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft } from 'merge-left-utils'
import { Button, CheckPicker, Form, Tag } from 'rsuite'
import useSearchParams from '@@partner:helpers/hooks/useSearchParams'

import './CouriersFilter.scss'
import { commonFieldChange } from '@@partner:helpers/util'
import { RsuiteItemDataType } from '@@partner:types:app'
import { useLazyFetchShops } from '@@partner:graphql/query/shop/shops.gql'
import { getSearchableShops, getSearchableZones } from '@@partner:helpers/order'
import { renderLazyPickerMenu } from '@components/common/pickers'
import { useLazyFetchDeliveryZones } from '@@partner:graphql/query/deliveryZone/allDeliveryZones.gql'
import { prepareCourierFilter } from '@@partner:helpers/courier'

export type CouriersFiltersType = {
  id?: GQL_ID
  name?: string
  phone?: string
  status?: number[]
  shopIds: GQL_ID[]
  zoneIds: GQL_ID[]
}

type CouriersFilterProps = {
  onFilterChanged: (filters: any) => void
}

const defaultFilter: CouriersFiltersType = {
  id: undefined,
  name: undefined,
  phone: undefined,
  status: undefined,
  shopIds: [],
  zoneIds: []
}

const searchableCourierStatuses = [
  {
    label: 'Активен',
    value: COURIER.FREE
  },
  {
    label: 'Неактивен',
    value: COURIER.INACTIVE
  },
  {
    label: 'Заблокирован',
    value: COURIER.BLOCKED
  }
]

export default function CouriersFilter ({
  onFilterChanged
}: CouriersFilterProps) {
  const { getSearchParams, setSearchParams } = useSearchParams()
  const [ state, setState ] = useState<CouriersFiltersType>(defaultFilter)
  const [ shops, setShops ] = useState<RsuiteItemDataType[]>([])
  const [ zones, setZones ] = useState<RsuiteItemDataType[]>([])

  const [ fetchShops, { data, loading } ] = useLazyFetchShops()
  const [ fetchZones, { data: zonesData, loading: loadingZones} ] = useLazyFetchDeliveryZones()

  useEffect(() => {
    if (!loading && data) {
      setShops(getSearchableShops(data.shops))
    }
  }, [ loading, data ])  

  useEffect(() => {
    if (!loadingZones && zonesData) {
      setZones(getSearchableZones(zonesData.allDeliveryZones.edges))
    }
  }, [ loadingZones, zonesData ])
  
  useEffect(() => {
    const searchParams = getSearchParams()
    const newState: CouriersFiltersType = mergeLeft(state, searchParams)
    setState(newState)
    onFilterChanged(prepareCourierFilter(newState))
  }, [])

  const handleFormSubmit = useCallback(() => {
    setSearchParams(state)
    onFilterChanged(prepareCourierFilter(state))
  }, [ onFilterChanged, state, setSearchParams ])

  const handleFormChange = (formValue: Partial<CouriersFiltersType>) => {
    setState((state) => mergeLeft(state, formValue))
  }

  const handleFieldChange = commonFieldChange(setState)

  return (
    <Form
      formValue={state}
      className='CouriersFilter'
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
    >
      <Form.Group className='CouriersFilter__Filters'>
        <Form.Control
          name='id'
          placeholder='ID'
          className='CouriersFilter__Filters__Input'
        />
        <Form.Control
          name='name'
          placeholder='ФИО'
          className='CouriersFilter__Filters__Input'
        />
        <Form.Control
          name='phone'
          placeholder='Номер телефона'
          className='CouriersFilter__Filters__Input'
        />
        <CheckPicker
          value={state.status}
          data={searchableCourierStatuses}
          searchable={false}
          placeholder="Статус"
          valueKey="value"
          labelKey="label"
          size="md"
          renderMenuItem={(label, item: any) => (
            <Tag key={item?.value} color={item?.color}>
              {label}
            </Tag>
          )}
          onChange={handleFieldChange('status')}
          className="CouriersFilter__Filters__Status"
        />
        <CheckPicker
          value={state.shopIds}
          data={shops}
          onOpen={fetchShops}
          searchable={false}
          placeholder="Магазины"
          valueKey="value"
          labelKey="label"
          size="md"
          style={{ maxWidth: 260 }}
          renderMenu={renderLazyPickerMenu(loading, data)}
          onChange={handleFieldChange('shopIds')}
        />
        <CheckPicker
          value={state.zoneIds}
          data={zones}
          onOpen={fetchZones}
          searchable={false}
          placeholder="Зоны доставки"
          valueKey="value"
          labelKey="label"
          size="md"
          style={{ maxWidth: 260 }}
          renderMenu={renderLazyPickerMenu(loadingZones, zonesData)}
          onChange={handleFieldChange('zoneIds')}
        />
      </Form.Group>

      <div className="ShopsFilter__Controls">
        <Button
          onClick={handleFormSubmit} 
          size="md" 
          color="green" 
          appearance="primary"
        >
          Поиск
        </Button>
      </div>
    </Form>
  )
}